import { gql } from "@apollo/client";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { formatISO, subDays } from "date-fns";
import { useMemo, useState } from "react";

import SosAlertDetailContent, {
  providerName,
  SOS_STATUS_LABEL,
  sosAlertAddress,
  sosAlertResolvedAt,
  statusColor,
  useSosTimestampFormat,
  visitAddress,
} from "~/components/command/SosAlertDetail";
import { useHistoricalSosAlertsQuery } from "~/graphql/__generated__/graphql";
import { Permission, usePermissionOrRedirect } from "~/hooks/permissions";

const HISTORY_LOOKBACK_DAYS = 30;

gql`
  query HistoricalSosAlerts($since: String!)
  @permission(requires: manageSosAlerts)
  {
    sosAlert(
      where: { createdAt: { gte: $since } }
      orderBy: { createdAt: { direction: desc, priority: 1 } }
      limit: 200
    ) {
      ...SosAlertDetail
    }
  }
`;

export default function SosAlertsHistoryView() {
  usePermissionOrRedirect(Permission.manageSosAlerts);
  const formatTimestamp = useSosTimestampFormat();

  const since = useMemo(
    () => formatISO(subDays(new Date(), HISTORY_LOOKBACK_DAYS)),
    [],
  );
  const { data, loading } = useHistoricalSosAlertsQuery({
    variables: { since },
    fetchPolicy: "cache-and-network",
  });

  const [openAlertId, setOpenAlertId] = useState<number | null>(null);

  const alerts = data?.sosAlert ?? [];
  const selectedAlert = alerts.find((alert) => alert.id === openAlertId) ?? null;

  return (
    <Box sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <WarningAmberIcon color="error" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          SOS alert history
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        SOS alerts from the last {HISTORY_LOOKBACK_DAYS} days.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Provider</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Triggered</TableCell>
              <TableCell>Resolved / canceled</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {alerts.map((alert) => {
              const resolvedAt = sosAlertResolvedAt(alert);
              const timezone = alert.visit?.addressTimezone ?? undefined;
              return (
                <TableRow
                  key={alert.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setOpenAlertId(alert.id)}
                >
                  <TableCell>{providerName(alert)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={statusColor(alert.status)}
                      label={SOS_STATUS_LABEL[alert.status]}
                    />
                  </TableCell>
                  <TableCell>{visitAddress(alert) ?? sosAlertAddress(alert) ?? "—"}</TableCell>
                  <TableCell>{formatTimestamp(alert.createdAt, timezone)}</TableCell>
                  <TableCell>
                    {resolvedAt ? formatTimestamp(resolvedAt, timezone) : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && alerts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No SOS alerts in this period.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={selectedAlert != null}
        onClose={() => setOpenAlertId(null)}
        maxWidth={false}
        slotProps={{ paper: { sx: { width: "calc(75vw + 48px)", maxWidth: "calc(75vw + 48px)" } } }}
      >
        {selectedAlert ? (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>SOS alert detail</DialogTitle>
            <DialogContent>
              <SosAlertDetailContent alert={selectedAlert} />
            </DialogContent>
          </>
        ) : null}
      </Dialog>
    </Box>
  );
}
